/**
 * メモ欄(textarea)の入力補完。
 *
 *   「//」 → 当日日付(JST)  例: 2026/08/01:
 *   「@」  → 「 / 自分の名前」(Cognitoのpreferred_username、無ければemail)
 *
 * JIRA/Confluenceと同様に、トリガー文字を打った直後に候補を表示し、
 * Enter で確定する。Escape または他のキー入力で候補は消える。
 *
 * 【日付をJSTで出す理由】
 * 利用者のPCのタイムゾーン設定に依存せず、常に日本時間の当日を入れたいため
 * toLocaleDateString に timeZone: 'Asia/Tokyo' を明示している。
 */
import { ref, computed, type Ref } from 'vue'
import { useAuthStore } from '@/stores/auth'

/** JSTの当日日付を「2026/08/01:」形式で返す */
export function todayJst(): string {
  const parts = new Date()
    .toLocaleDateString('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    // ja-JP は「2026/08/01」形式だが、環境差を吸収するため数値だけ取り出す
    .match(/\d+/g)
  if (!parts || parts.length < 3) return ''
  return `${parts[0]}/${parts[1]}/${parts[2]}:`
}

export interface Suggestion {
  /** 候補に表示するラベル */
  label: string
  /** 実際に挿入される文字列 */
  value: string
  /** 置き換える対象のトリガー文字数(「//」なら2、「@」なら1) */
  triggerLength: number
}

/**
 * @param model      textareaにv-modelしているref
 * @param elRef      textarea要素のref
 * @param enableUser 「@」補完を有効にするか(セラー向け欄では名前挿入をしないなど)
 */
export function useMemoAutocomplete(
  model: Ref<string>,
  elRef: Ref<HTMLTextAreaElement | null>,
  enableUser = true,
) {
  const auth = useAuthStore()
  const suggestion = ref<Suggestion | null>(null)

  const isOpen = computed(() => suggestion.value !== null)

  function close() {
    suggestion.value = null
  }

  /**
   * 入力のたびに、キャレット直前がトリガー文字かを判定する。
   * トリガー直後にだけ候補を出し、続けて文字を打てば自然に消える。
   */
  function onInput() {
    const el = elRef.value
    if (!el) return close()
    const pos = el.selectionStart ?? 0
    const before = model.value.slice(0, pos)

    if (before.endsWith('//')) {
      const date = todayJst()
      if (!date) return close()
      suggestion.value = { label: `日付を挿入  ${date}`, value: date, triggerLength: 2 }
      return
    }

    if (enableUser && before.endsWith('@')) {
      const name = auth.memberName
      // 属性が取れない場合は候補を出さない(空文字を挿入してしまわないように)
      if (!name) return close()
      // 「(半角スペース)/(半角スペース)名前」の形式。
      // 既存の内部連絡事項が「内容 / 担当者」の書式のため、それに揃える。
      const value = ` / ${name}`
      suggestion.value = { label: `名前を挿入  ${value.trim()}`, value, triggerLength: 1 }
      return
    }

    close()
  }

  /** 候補を確定し、トリガー文字を置き換える */
  function accept() {
    const el = elRef.value
    const sug = suggestion.value
    if (!el || !sug) return

    const pos = el.selectionStart ?? 0
    const start = pos - sug.triggerLength
    model.value = model.value.slice(0, start) + sug.value + model.value.slice(pos)
    close()

    // 挿入した文字列の直後へキャレットを移す
    const next = start + sug.value.length
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(next, next)
    })
  }

  /**
   * 候補表示中の Enter は改行ではなく確定に使う。
   * 候補が出ていないときは通常どおり改行させる(preventDefaultしない)。
   */
  function onKeydown(e: KeyboardEvent) {
    if (!suggestion.value) return
    if (e.key === 'Enter') {
      e.preventDefault()
      accept()
      return
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      close()
    }
  }

  return { suggestion, isOpen, onInput, onKeydown, accept, close }
}
